import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  Button,
  Check,
  EditOutline,
  Icon,
  RotatingSpinner,
  TextInput,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import useApiPatch from 'Pods/Shared/Hooks/useApiPatch'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import { patchSessionTitleInLibraryCache } from 'Pods/ThreeDStudio/Shared/Utils/patchSessionTitleInLibraryCache'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setOpenedModelTitle } from 'Pods/ThreeDStudio/Store/actions/ui'

function ModelTitle() {
  const queryClient = useQueryClient()
  const title = useThreeDStudioStore(state => state.ui?.openedModel.title ?? state.ui?.openedModel.prompt)
  const generatorSessionId = useThreeDStudioStore(state => state.generation?.generatorSessionId)
  const { patchData, isLoading } = useApiPatch()
  const { captureException } = useSentry()
  const isReadOnly = useOpenedModelReadOnly()
  const [isEditing, setIsEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState(title ?? '')

  useEffect(() => {
    if (!isEditing) {
      setDraftTitle(title ?? '')
    }
  }, [isEditing, title])

  useEffect(() => {
    setIsEditing(false)
  }, [generatorSessionId])

  const handleStartEditing = useCallback(() => {
    setIsEditing(true)
  }, [])

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleChange = useCallback((e) => {
    setDraftTitle(e.target.value)
  }, [])

  const isSaveDisabled = useMemo(() => (
    !draftTitle
    || draftTitle.trim() === ''
    || (title && draftTitle.trim() === title.trim())
  ), [draftTitle, title])

  const handleConfirm = useCallback(() => {
    if (isLoading) return

    if (isSaveDisabled) {
      handleCancelEditing()
      return
    }

    const nextTitle = draftTitle.trim()

    patchData({
      path: `/api/internal/generator/sessions/${generatorSessionId}`,
      data: { data: { title: nextTitle } },
      onSuccess: () => {
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification('Model title updated successfully.', { type: 'success', autohide: true })
        }
        setOpenedModelTitle(nextTitle)
        patchSessionTitleInLibraryCache(queryClient, {
          sessionId: generatorSessionId,
          title: nextTitle,
        })
        setIsEditing(false)
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update model title.'
        captureException(error)
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification(errorMessage, { type: 'error', autohide: true })
        }
        setIsEditing(false)
      },
    })
  }, [
    captureException,
    draftTitle,
    generatorSessionId,
    handleCancelEditing,
    isLoading,
    isSaveDisabled,
    patchData,
    queryClient,
  ])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEditing()
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirm()
    }
  }, [handleCancelEditing, handleConfirm])

  const confirmIcon = isLoading
    ? <Icon svgComponent={RotatingSpinner} color="dark" size="small" notSpaced disabled />
    : <Icon svgComponent={Check} color="dark" size="small" notSpaced />

  if (!title) return <div className="model-title" aria-hidden="true" />

  return (
    <div className="model-title">
      {isEditing ? (
        <>
          <TextInput
            id="model-title"
            value={draftTitle}
            size="small"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            isDisabled={isLoading}
            isPilot
          />
          <Button
            customClass="model-title__edit-button"
            type="secondary"
            size="tiny"
            variant="only-icon"
            aria-label="Confirm model title"
            onClick={handleConfirm}
            disabled={isLoading}
            icon={confirmIcon}
          />
        </>
      ) : (
        <>
          <span className="model-title__text cgt-heading-50">{title}</span>
          {!isReadOnly && (
            <Button
              customClass="model-title__edit-button"
              type="secondary"
              size="tiny"
              variant="only-icon"
              aria-label="Edit model title"
              onClick={handleStartEditing}
              icon={<Icon svgComponent={EditOutline} color="dark" size="small" notSpaced />}
            />
          )}
        </>
      )}
    </div>
  )
}

export default memo(ModelTitle)

